import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Loader2, TrendingUp, Globe, Eye, FileText } from 'lucide-react';

interface TrafficStats {
  totalViews: number;
  topPages: { path: string; views: number }[];
  dailyViews: { date: string; views: number }[];
  countryBreakdown: { country: string; views: number }[];
}

export const TrafficDashboard = () => {
  const [stats, setStats] = useState<TrafficStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => { fetchStats(); }, [days]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await api.get<TrafficStats>(`/traffic/stats?days=${days}`);
      setStats(data);
    } catch (err) {
      console.error('Failed to load traffic stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8651A]" />
      </div>
    );
  }

  const maxDaily = Math.max(...(stats?.dailyViews.map(d => d.views) || [1]), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#f5f5f5] tracking-tight">Traffic</h1>
          <p className="text-sm text-[#666] mt-1">Page view analytics</p>
        </div>
        <select
          value={days}
          onChange={e => setDays(parseInt(e.target.value))}
          className="bg-[#0a0a0a] border border-[#222] rounded px-3 py-2 text-sm text-[#f5f5f5]"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#0a0a0a] border border-[#222] rounded p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-[#E8651A]/10">
              <Eye className="w-5 h-5 text-[#E8651A]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#f5f5f5]">{stats?.totalViews || 0}</p>
              <p className="text-xs text-[#555]">Total Page Views</p>
            </div>
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-[#222] rounded p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-[#E8651A]/10">
              <FileText className="w-5 h-5 text-[#E8651A]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#f5f5f5]">{stats?.topPages.length || 0}</p>
              <p className="text-xs text-[#555]">Unique Pages</p>
            </div>
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-[#222] rounded p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-[#E8651A]/10">
              <Globe className="w-5 h-5 text-[#E8651A]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#f5f5f5]">{stats?.countryBreakdown.length || 0}</p>
              <p className="text-xs text-[#555]">Countries</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily chart */}
        <div className="bg-[#0a0a0a] border border-[#222] rounded p-6">
          <h3 className="text-sm font-semibold text-[#f5f5f5] mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#E8651A]" /> Daily Views
          </h3>
          <div className="space-y-1">
            {stats?.dailyViews.slice(0, 14).reverse().map(d => (
              <div key={d.date} className="flex items-center gap-3">
                <span className="text-xs text-[#555] w-20 text-right">{new Date(d.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
                <div className="flex-1 bg-[#111] rounded h-5 overflow-hidden">
                  <div
                    className="h-full rounded transition-all"
                    style={{
                      width: `${(d.views / maxDaily) * 100}%`,
                      background: 'linear-gradient(90deg, #E8651A, #F5922B)',
                      minWidth: d.views > 0 ? '2px' : '0',
                    }}
                  />
                </div>
                <span className="text-xs text-[#f5f5f5] w-8 text-right font-mono">{d.views}</span>
              </div>
            ))}
            {(!stats?.dailyViews.length) && <p className="text-sm text-[#555] py-4 text-center">No data yet</p>}
          </div>
        </div>

        {/* Top pages */}
        <div className="bg-[#0a0a0a] border border-[#222] rounded p-6">
          <h3 className="text-sm font-semibold text-[#f5f5f5] mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#E8651A]" /> Top Pages
          </h3>
          <div className="space-y-2">
            {stats?.topPages.map((p, i) => (
              <div key={p.path} className="flex items-center gap-3">
                <span className="text-xs text-[#555] w-5">{i + 1}</span>
                <span className="text-sm text-[#f5f5f5] flex-1 truncate font-mono">{p.path}</span>
                <span className="text-xs text-[#E8651A] font-mono">{p.views}</span>
              </div>
            ))}
            {(!stats?.topPages.length) && <p className="text-sm text-[#555] py-4 text-center">No data yet</p>}
          </div>
        </div>
      </div>

      {/* Countries */}
      {stats?.countryBreakdown && stats.countryBreakdown.length > 0 && (
        <div className="mt-6 bg-[#0a0a0a] border border-[#222] rounded p-6">
          <h3 className="text-sm font-semibold text-[#f5f5f5] mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#E8651A]" /> Countries
          </h3>
          <div className="flex flex-wrap gap-3">
            {stats.countryBreakdown.map(c => (
              <span key={c.country} className="bg-[#111] border border-[#222] rounded px-3 py-1.5 text-xs text-[#f5f5f5]">
                {c.country} <span className="text-[#E8651A] ml-1">{c.views}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
